import { Megaphone, Cake, Plane, Sparkles } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import type { AnnouncementRecord } from '../../lib/types';

// "For You" block — feed of announcements + HR signals + bits of personality.
// Live data:    announcements (already implemented)
// Soon (W4):   birthdays this week, who's out today, payroll status
// These render as muted "Soon" entries so the block is never empty.

interface Item {
  id: string;
  icon: typeof Megaphone;
  iconClass: string;
  title: string;
  body?: string;
  soon?: boolean;
  href?: string;
}

export const ForYou = () => {
  const announcements = useApi<AnnouncementRecord[]>('/api/announcements/active');

  const items: Item[] = [];

  for (const a of announcements.data ?? []) {
    items.push({
      id: a.id,
      icon: Megaphone,
      iconClass: 'text-brand-orange bg-brand-orange/15',
      title: a.title,
      body: a.body,
    });
  }

  // Soon-stubs — placeholders that LOOK like the eventual W4 feed so the block
  // has texture from day one and the eventual data lands without UI changes.
  items.push({
    id: 'soon-birthday',
    icon: Cake,
    iconClass: 'text-status-warning bg-status-warning/15',
    title: 'Team birthdays',
    body: "Coming soon — we'll surface teammate birthdays for the week.",
    soon: true,
  });
  items.push({
    id: 'soon-outs',
    icon: Plane,
    iconClass: 'text-status-info bg-status-info/15',
    title: "Who's out today",
    body: 'Coming soon — see teammate leave at a glance.',
    soon: true,
  });

  return (
    <section aria-label="For you" className="rounded-2xl border border-line bg-bg-primary p-4 shadow-sm sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-brand-orange" aria-hidden="true" />
        <h2 className="text-sm font-semibold tracking-tight text-text-primary">For you</h2>
      </div>

      {announcements.loading && items.length === 0 ? (
        <div className="text-sm text-text-tertiary">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-text-tertiary">
          Quiet day — nothing new on the noticeboard.
        </div>
      ) : (
        <ul className="-mx-1 space-y-0.5">
          {items.map((it) => (
            <li
              key={it.id}
              aria-disabled={it.soon ? 'true' : undefined}
              className={[
                'flex items-start gap-3 rounded-md px-1 py-2',
                it.soon ? 'opacity-60' : '',
              ].join(' ')}
            >
              <span
                className={[
                  'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md',
                  it.soon ? 'bg-text-tertiary/15 text-text-tertiary' : it.iconClass,
                ].join(' ')}
                aria-hidden="true"
              >
                <it.icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <p
                    className={[
                      'truncate text-sm font-medium',
                      it.soon ? 'text-text-tertiary' : 'text-text-primary',
                    ].join(' ')}
                  >
                    {it.title}
                  </p>
                  {it.soon && (
                    <span className="shrink-0 rounded-full bg-bg-secondary px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-tertiary">
                      Coming soon
                    </span>
                  )}
                </div>
                {it.body && (
                  <p
                    className={[
                      'mt-0.5 line-clamp-2 text-xs',
                      it.soon ? 'text-text-tertiary' : 'text-text-secondary',
                    ].join(' ')}
                  >
                    {it.body}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
